import clsx from 'clsx'
import type * as React from 'react'

export enum AnnouncementType {
  Simple = 'simple',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
  Important = 'important',
  Cancelled = 'cancelled',
  Unstyled = 'unstyled',
}

export enum IconType {
  Info = 'fa-circle-info',
  Warning = 'fa-warning',
  Success = 'fa-circle-check',
  Simple = 'fa-calendar',
  Cancel = 'fa-calendar-xmark',
  None = '',
}

interface AnnouncementProps {
  type: AnnouncementType
  title?: React.ReactNode
  subtitle?: React.ReactNode
  message?: React.ReactNode
  icon?: IconType | string
}

export function Announcement(props: AnnouncementProps) {
  const { type, title, subtitle, message } = props

  const getIconForType = (announcementType: AnnouncementType): string => {
    switch (announcementType) {
      case AnnouncementType.Simple:
        return IconType.Simple
      case AnnouncementType.Info:
        return IconType.Info
      case AnnouncementType.Warning:
        return IconType.Warning
      case AnnouncementType.Error:
        return IconType.Warning // Using warning icon for error
      case AnnouncementType.Success:
        return IconType.Success
      case AnnouncementType.Important:
        return IconType.Info // Using info icon for important
      case AnnouncementType.Unstyled:
        return IconType.None
      default:
        return IconType.Info
    }
  }

  const icon = props.icon || getIconForType(type)

  const iconClasses = clsx('fa-solid fa-sharp', {
    [icon]: true,
  })

  const announcementClasses = clsx('font-normal flex flex-col gap-2 text-wrap', {
    'p-4 rounded bg-sand-25 border-gray-stroke border text-charcoal-50':
      type === AnnouncementType.Simple,
    'p-4 rounded bg-sky-50 text-indigo': type === AnnouncementType.Info,
    'p-4 rounded bg-mint-25 text-forest': type === AnnouncementType.Success,
    'p-4 rounded bg-lavender-25 text-indigo': type === AnnouncementType.Important,
    'p-4 rounded bg-gold-25 text-taupe-dark': type === AnnouncementType.Warning,
    'p-4 rounded bg-peach-25 text-brick-light': type === AnnouncementType.Error,
    'p-4 rounded bg-sand-25 border-gray-stroke border text-charcoal-50 line-through':
      type === AnnouncementType.Cancelled,
    'bg-transparent text-charcoal-50': type === AnnouncementType.Unstyled,
  })

  const messageClasses = clsx('font-normal', {
    'text-charcoal-50': type === AnnouncementType.Simple,
  })

  return (
    <div className={announcementClasses}>
      {type !== AnnouncementType.Unstyled && (title || subtitle) && (
        <div className="flex flex-row items-start gap-4">
          {title && (
            <div>
              <i className={iconClasses}></i>
            </div>
          )}
          <div className="flex-grow">
            {title && <p className="font-semibold mb-1 text-wrap">{title}</p>}
            {subtitle && <div className="mb-1 [&_p]:mt-2">{subtitle}</div>}
          </div>
        </div>
      )}
      {message && (
        <div className="flex flex-row items-start gap-4">
          {type !== AnnouncementType.Unstyled && icon && !title && !subtitle && (
            <div>
              <i className={iconClasses}></i>
            </div>
          )}
          <div className={messageClasses}>{message}</div>
        </div>
      )}
    </div>
  )
}
